import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";

import {
  addDays,
  format,
  isValid,
  parse,
  parseISO,
  startOfDay,
  subDays,
} from "date-fns";

import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-date-fns";

import {
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import { useMediaQuery } from "react-responsive";

import InitContext from "../pages/InitContext";
import useMmcService from "../../services/MmcService";
import Spinner from "../spinner/Spinner";
import ErrorMessage from "../errorMessage/ErrorMessage";
import useDialogDrag from "../../hooks/useDialogDrag";

ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
);

const SYMPTOM_MIN = 1;
const SYMPTOM_MAX = 5;
const MEDICATION_AREA_MAX_RATIO = 0.4;
const MEDICATION_LANE_MIN_HEIGHT_PX = 36;
const MEDICATION_AREA_PADDING_PX = 28;
const CHART_SECTION_GAP_PX = 0;
const CHART_AXIS_WIDTH_DESKTOP_PX = 88;
const CHART_AXIS_WIDTH_MOBILE_PX = 76;
const CHART_TICK_ROTATION_DEG = 35;
const MEDICATION_LABEL_ROTATION_DEG = -28;
const LEGEND_MAX_VISIBLE_ITEMS = 5;
const LEGEND_SCROLL_HEIGHT_PX = 120;
const MOBILE_BREAKPOINT = 768;

const SYMPTOM_PALETTE = [
  "#1976d2",
  "#00897b",
  "#f4511e",
  "#8e24aa",
  "#2e7d32",
  "#ef6c00",
  "#5e35b1",
  "#00838f",
  "#c62828",
  "#3949ab",
  "#6d4c41",
  "#7cb342",
  "#00acc1",
  "#ad1457",
  "#546e7a",
  "#43a047",
  "#1e88e5",
  "#fb8c00",
  "#7e57c2",
  "#039be5",
];

const MEDICATION_PALETTE = [
  "#1565c0",
  "#2e7d32",
  "#d81b60",
  "#6a1b9a",
  "#ef6c00",
  "#00838f",
  "#c62828",
  "#455a64",
  "#43a047",
  "#5e35b1",
  "#f9a825",
  "#00897b",
  "#3949ab",
  "#7cb342",
  "#039be5",
  "#8d6e63",
  "#e53935",
  "#1e88e5",
  "#00acc1",
  "#7e57c2",
];

const symptomsMedicationBandsPlugin = {
  id: "symptomsMedicationBands",
  beforeDraw: (chart, _args, pluginOptions) => {
    const { ctx, chartArea, scales } = chart;
    const yScale = scales?.y;

    if (!chartArea || !yScale) {
      return;
    }

    const symptomsBackgroundColor = pluginOptions?.symptomsBackgroundColor || "#f6fbff";
    const medicationsBackgroundColor = pluginOptions?.medicationsBackgroundColor || "#dceefe";
    const sectionMode = pluginOptions?.sectionMode || "split";

    ctx.save();
    if (sectionMode === "symptoms") {
      ctx.fillStyle = symptomsBackgroundColor;
      ctx.fillRect(
        chartArea.left,
        chartArea.top,
        chartArea.right - chartArea.left,
        chartArea.bottom - chartArea.top,
      );
      ctx.restore();
      return;
    }

    if (sectionMode === "medications") {
      ctx.fillStyle = medicationsBackgroundColor;
      ctx.fillRect(
        chartArea.left,
        chartArea.top,
        chartArea.right - chartArea.left,
        chartArea.bottom - chartArea.top,
      );
      ctx.restore();
      return;
    }

    const splitValue = SYMPTOM_MAX + 0.5;
    const splitY = yScale.getPixelForValue(splitValue);
    const clampedSplitY = Math.max(chartArea.top, Math.min(chartArea.bottom, splitY));

    ctx.fillStyle = medicationsBackgroundColor;
    ctx.fillRect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      clampedSplitY - chartArea.top,
    );

    ctx.fillStyle = symptomsBackgroundColor;
    ctx.fillRect(
      chartArea.left,
      clampedSplitY,
      chartArea.right - chartArea.left,
      chartArea.bottom - clampedSplitY,
    );

    ctx.restore();
  },
  afterDraw: (chart, _args, pluginOptions) => {
    const { ctx, chartArea, scales } = chart;
    const yScale = scales?.y;

    if (!chartArea || !yScale) {
      return;
    }

    const medicationLabel = pluginOptions?.medicationAxisLabel;
    const severityLabel = pluginOptions?.severityAxisLabel;

    if (!medicationLabel && !severityLabel) {
      return;
    }

    const sectionMode = pluginOptions?.sectionMode || "split";
    const splitY = yScale.getPixelForValue(SYMPTOM_MAX + 0.5);
    const clampedSplitY = Math.max(chartArea.top, Math.min(chartArea.bottom, splitY));
    const medicationCenterY = sectionMode === "medications"
      ? chartArea.top + ((chartArea.bottom - chartArea.top) / 2)
      : chartArea.top + (clampedSplitY - chartArea.top) / 2;
    const severityCenterY = sectionMode === "symptoms"
      ? chartArea.top + ((chartArea.bottom - chartArea.top) / 2)
      : clampedSplitY + (chartArea.bottom - clampedSplitY) / 2;
    // Labels bewusst links vom y-Achsen-Tickbereich platzieren.
    const axisLabelMinGutterX = pluginOptions?.axisLabelMinGutterX ?? 14;
    const leftGutterX = Math.max(axisLabelMinGutterX, yScale.left - 56);
    const axisLabelOffsetX = pluginOptions?.axisLabelOffsetX ?? 20;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    if (medicationLabel) {
      ctx.fillStyle = pluginOptions?.medicationLabelColor || "#1565a0";
      ctx.font = "600 12px Arial, sans-serif";
      ctx.save();
      ctx.translate(leftGutterX + axisLabelOffsetX, medicationCenterY);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(medicationLabel, 0, 0);
      ctx.restore();
    }

    if (severityLabel) {
      ctx.fillStyle = pluginOptions?.severityLabelColor || "#1565a0";
      ctx.font = "600 12px Arial, sans-serif";
      ctx.save();
      ctx.translate(leftGutterX + axisLabelOffsetX, severityCenterY);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(severityLabel, 0, 0);
      ctx.restore();
    }

    if (sectionMode !== "split") {
      ctx.restore();
      return;
    }

    const axisRight = Number.isFinite(yScale.right) ? yScale.right : chartArea.left;
    const separatorLeftX = Math.max(0, leftGutterX - 6);
    const separatorRightX = Math.max(separatorLeftX + 12, axisRight - 2);
    const separatorY = Math.round(clampedSplitY) + 0.5;
    ctx.save();
    ctx.strokeStyle = pluginOptions?.separatorColor || "#90caf9";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(separatorLeftX, separatorY);
    ctx.lineTo(separatorRightX, separatorY);
    ctx.stroke();
    ctx.restore();

    ctx.restore();
  },
};

const parseFlexibleDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const candidate = value.trim();
  if (!candidate) {
    return null;
  }

  const parsedIso = parseISO(candidate);
  if (isValid(parsedIso)) {
    return parsedIso;
  }

  const formats = [
    "dd/MM/yyyy HH:mm",
    "dd/MM/yyyy",
    "dd.MM.yyyy HH:mm",
    "dd.MM.yyyy",
    "yyyy-MM-dd HH:mm",
    "yyyy-MM-dd",
  ];

  for (let i = 0; i < formats.length; i += 1) {
    const parsed = parse(candidate, formats[i], new Date());
    if (isValid(parsed)) {
      return parsed;
    }
  }

  return null;
};

const formatTooltipDateOnly = (date) => {
  const parsed = parseFlexibleDate(date);
  return parsed ? format(parsed, "dd/MM/yyyy") : "-";
};

const isSameMoment = (startDate, endDate) => {
  const start = parseFlexibleDate(startDate);
  const end = parseFlexibleDate(endDate);

  if (!start || !end) {
    return false;
  }

  return start.getTime() === end.getTime();
};

const toSeverity = (value) => {
  const severity = Number(value);
  if (!Number.isFinite(severity)) {
    return null;
  }

  return Math.min(SYMPTOM_MAX, Math.max(SYMPTOM_MIN, severity));
};

const stableColorForKey = (key, palette) => {
  const resolvedPalette = palette?.length ? palette : SYMPTOM_PALETTE;

  if (!key) {
    return resolvedPalette[0];
  }

  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }

  return resolvedPalette[Math.abs(hash) % resolvedPalette.length];
};

const normalizeSymptoms = (symptoms) => {
  return (symptoms || [])
    .map((symptom) => {
      const term = symptom?.term || "Unknown";
      const severity = toSeverity(symptom?.sufferingLevel);
      const start = parseFlexibleDate(symptom?.startDate);
      const rawEnd = parseFlexibleDate(symptom?.endDate);

      if (!start || severity === null) {
        return null;
      }

      // Ohne Enddatum nur einen einzelnen Symptom-Punkt zeichnen (keine offene Linie bis heute).
      const safeEnd = rawEnd && rawEnd >= start ? rawEnd : start;

      return {
        id: symptom?.id || `${term}-${start.getTime()}-${severity}`,
        term,
        severity,
        start,
        end: safeEnd,
        hasExplicitEndDate: !!rawEnd,
        // Use for the chart position only the date without time
        plotStart: startOfDay(start),
        plotEnd: startOfDay(safeEnd),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.start.getTime() - b.start.getTime());
};

const normalizeMedications = (drugs) => {
  const now = new Date();

  return (drugs || [])
    .map((drug) => {
      const start = parseFlexibleDate(drug?.startDate);

      const explicitEnd =
        parseFlexibleDate(drug?.endDate) ||
        parseFlexibleDate(drug?.stopDate) ||
        parseFlexibleDate(drug?.deactivationDate);

      const rawEnd = explicitEnd || now;

      if (!start) {
        return null;
      }

      const end = rawEnd >= start ? rawEnd : start;

      return {
        id: drug?.id || `${drug?.activeSubstance || "drug"}-${start.getTime()}`,
        productName: drug?.productName || "Unknown",
        activeSubstance: drug?.activeSubstance || "Unknown",
        start,
        end,
        hasExplicitEndDate: !!explicitEnd,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.start.getTime() - b.start.getTime());
};

const buildLineSegments = (items, getGroupKey, getY, getMeta, palette, shouldForceRange) => {
  // Chart.js time scale expects an x-value for each Object data
  // Therefore we create an own 2-point section foe each interval instead of null delimiters
  return items.map((item) => {
    const groupKey = getGroupKey(item);
    const startX = item.plotStart || item.start;
    const endX = item.plotEnd || item.end;
    const y = getY(item);
    const meta = getMeta(item);
    const isSameX = startX?.getTime?.() === endX?.getTime?.();
    const forceRange = typeof shouldForceRange === "function" ? shouldForceRange(item) : false;

    return {
      groupKey,
      color: stableColorForKey(groupKey, palette),
      data: isSameX && !forceRange
        ? [{ x: startX, y, meta }]
        : [
          { x: startX, y, meta },
          { x: endX, y, meta },
        ],
    };
  });
};

const buildInitialVisibility = (keys) => {
  const result = {};
  keys.forEach((key) => {
    result[key] = true;
  });
  return result;
};

const getMedicationKey = (medication) => {
  return `${medication?.activeSubstance || "Unknown"}||${medication?.productName || "Unknown"}`;
};

const formatActiveSubstanceLabel = (value) => {
  return (value || "Unknown").replace(/&&&/g, ",");
};

const getMedicationLabel = (medication, substance) => {
  const activeSubstance = formatActiveSubstanceLabel(medication?.activeSubstance);
  const productName = medication?.productName || "Unknown";
  return `${productName} (${substance}: ${activeSubstance})`;
};

const SymptomsLineChart = ({ symptoms, patientId, onCloseDialog, showDialog }) => {
  const { t } = useTranslation();
  const context = useContext(InitContext);
  const symptomChartRef = useRef(null);
  const medicationChartRef = useRef(null);
  const isMobile = useMediaQuery({ maxWidth: MOBILE_BREAKPOINT });
  const { isDragging, dragStyle, onDragHandleMouseDown } = useDialogDrag(showDialog, {
    baseTransform: "translate(-50%, -50%)",
  });
  const [showMedications, setShowMedications] = useState(true);
  const [showSymptomsCardContent, setShowSymptomsCardContent] = useState(false);
  const [showMedicationsCardContent, setShowMedicationsCardContent] = useState(false);
  const [termVisibility, setTermVisibility] = useState({});
  const [medicationVisibility, setMedicationVisibility] = useState({});
  const [medicationAxisLabels, setMedicationAxisLabels] = useState([]);
  const [activeMedicationAxisLabel, setActiveMedicationAxisLabel] = useState(null);
  const [viewRange, setViewRange] = useState(null);
  const { loading, error, clearError, getDbDrugs } = useMmcService();
  const [medications, setMedications] = useState([]);
  const resolvedPatientId = patientId || context?.patient?.id;

  useEffect(() => {
    if (!showDialog || !resolvedPatientId || !context?.token) {
      return;
    }

    let mounted = true;

    const loadMedications = async () => {
      clearError();
      const drugs = await getDbDrugs(context?.token, resolvedPatientId, false, false);

      if (mounted) {
        setMedications(drugs || []);
      }
    };

    loadMedications().then();

    return () => {
      mounted = false;
    };
  }, [showDialog, resolvedPatientId, context?.token]);

  const normalizedSymptoms = useMemo(() => normalizeSymptoms(symptoms), [symptoms]);
  const normalizedMedications = useMemo(() => normalizeMedications(medications), [medications]);

  const termKeys = useMemo(
    () => Array.from(new Set(normalizedSymptoms.map((item) => item.term))),
    [normalizedSymptoms],
  );

  const medicationEntries = useMemo(
    () => {
      const entriesByKey = new Map();

      normalizedMedications.forEach((item) => {
        const key = getMedicationKey(item);

        if (!entriesByKey.has(key)) {
          entriesByKey.set(key, {
            key,
            label: getMedicationLabel(item, t("active_substance")),
            activeSubstance: item.activeSubstance,
          });
        }
      });

      return Array.from(entriesByKey.values()).sort((a, b) => a.label.localeCompare(b.label));
    },
    [normalizedMedications, t],
  );

  useEffect(() => {
    setTermVisibility((prev) => ({ ...buildInitialVisibility(termKeys), ...prev }));
  }, [termKeys.join("|")]);

  useEffect(() => {
    setMedicationVisibility((prev) => ({
      ...buildInitialVisibility(medicationEntries.map((entry) => entry.key)),
      ...prev,
    }));
  }, [medicationEntries]);

  const visibleSymptoms = useMemo(
    () => normalizedSymptoms.filter((item) => termVisibility[item.term] !== false),
    [normalizedSymptoms, termVisibility],
  );

  const visibleMedications = useMemo(() => {
    if (!showMedications) {
      return [];
    }

    return normalizedMedications.filter(
      (item) => medicationVisibility[getMedicationKey(item)] !== false,
    );
  }, [normalizedMedications, showMedications, medicationVisibility]);

  const medicationLaneEntries = useMemo(() => {
    const entriesByKey = new Map();

    visibleMedications.forEach((item) => {
      const key = getMedicationKey(item);

      if (!entriesByKey.has(key)) {
        entriesByKey.set(key, {
          key,
          label: formatActiveSubstanceLabel(item.activeSubstance),
        });
      }
    });

    return Array.from(entriesByKey.values()).map((entry, index) => ({
      ...entry,
      laneValue: index + 1,
    }));
  }, [visibleMedications, t]);

  const medicationLaneByKey = useMemo(() => {
    const map = {};

    medicationLaneEntries.forEach(({ key, laneValue }) => {
      map[key] = laneValue;
    });

    return map;
  }, [medicationLaneEntries]);

  const symptomSegments = useMemo(
    () =>
      buildLineSegments(
        visibleSymptoms,
        (item) => item.term,
        (item) => item.severity,
        (item) => ({
          type: "symptom",
          term: t(item.term),
          severity: item.severity,
          startDate: item.start,
          endDate: item.end,
        }),
        SYMPTOM_PALETTE,
        (item) => item.hasExplicitEndDate,
      ),
    [visibleSymptoms, t],
  );

  const medicationSegments = useMemo(
    () =>
      buildLineSegments(
        visibleMedications,
        (item) => getMedicationKey(item),
        (item) => medicationLaneByKey[getMedicationKey(item)] || 1,
        (item) => ({
          type: "medication",
          label: formatActiveSubstanceLabel(item.activeSubstance),
          productName: item.productName,
          activeSubstance: item.activeSubstance,
          startDate: item.start,
          endDate: item.end,
          hasExplicitEndDate: item.hasExplicitEndDate,
        }),
        MEDICATION_PALETTE,
      ),
    [visibleMedications, medicationLaneByKey, t],
  );

  const allDatePoints = useMemo(() => {
    const points = [];
    visibleSymptoms.forEach((item) => {
      points.push(item.plotStart || item.start, item.plotEnd || item.end);
    });
    visibleMedications.forEach((item) => {
      points.push(item.start, item.end);
    });
    return points;
  }, [visibleSymptoms, visibleMedications]);

  const initialRange = useMemo(() => {
    if (!allDatePoints.length) {
      const now = new Date();
      return { min: subDays(now, 7), max: addDays(now, 7) };
    }

    const min = new Date(Math.min(...allDatePoints.map((d) => d.getTime())));
    const max = new Date(Math.max(...allDatePoints.map((d) => d.getTime())));
    return { min: subDays(min, 1), max: addDays(max, 1) };
  }, [allDatePoints]);

  useEffect(() => {
    setViewRange((prev) => prev || initialRange);
  }, [initialRange]);

  const symptomDatasets = useMemo(
    () => symptomSegments.flatMap((segment) => {
      const baseLabel = `symptom:${segment.groupKey}`;

      return [
        {
          label: baseLabel,
          data: segment.data,
          borderColor: segment.color,
          backgroundColor: segment.color,
          tooltipEnabled: true,
          borderWidth: 4,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointHitRadius: 12,
          tension: 0,
          spanGaps: false,
        },
        {
          label: baseLabel,
          data: segment.data,
          borderColor: "rgba(0, 0, 0, 0)",
          backgroundColor: "rgba(0, 0, 0, 0)",
          tooltipEnabled: false,
          borderWidth: 14,
          pointRadius: 0,
          pointHoverRadius: 0,
          pointHitRadius: 0,
          tension: 0,
          spanGaps: false,
        },
      ];
    }),
    [symptomSegments],
  );

  const medicationDatasets = useMemo(
    () => medicationSegments.map((segment) => ({
      label: `med:${segment.groupKey}`,
      data: segment.data,
      borderColor: segment.color,
      backgroundColor: segment.color,
      tooltipEnabled: true,
      borderWidth: 6,
      borderDash: [8, 4],
      pointStyle: "rectRounded",
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0,
      spanGaps: false,
    })),
    [medicationSegments],
  );

  const symptomChartData = useMemo(() => ({ datasets: symptomDatasets }), [symptomDatasets]);
  const medicationChartData = useMemo(() => ({ datasets: medicationDatasets }), [medicationDatasets]);

  const hasMedicationSection = showMedications && medicationLaneEntries.length > 0;
  const chartAxisWidth = isMobile ? CHART_AXIS_WIDTH_MOBILE_PX : CHART_AXIS_WIDTH_DESKTOP_PX;
  const medicationLaneMinHeightPx = isMobile ? 52 : MEDICATION_LANE_MIN_HEIGHT_PX;
  const visibleMedicationAreaHeight = hasMedicationSection ? `${MEDICATION_AREA_MAX_RATIO * 100}%` : "0%";
  const visibleSymptomAreaHeight = hasMedicationSection ? `${(1 - MEDICATION_AREA_MAX_RATIO) * 100}%` : "100%";
  const medicationContentHeight = hasMedicationSection
    ? (medicationLaneEntries.length * medicationLaneMinHeightPx) + MEDICATION_AREA_PADDING_PX
    : 0;
  const medicationChartCanvasHeight = hasMedicationSection ? medicationContentHeight : 0;

  const shouldScrollSymptomsLegend = termKeys.length > LEGEND_MAX_VISIBLE_ITEMS;
  const shouldScrollMedicationsLegend = medicationEntries.length > LEGEND_MAX_VISIBLE_ITEMS;

  const createRangeUpdater = useCallback((chart) => {
    const xScale = chart?.scales?.x;
    if (xScale) {
      setViewRange({ min: new Date(xScale.min), max: new Date(xScale.max) });
    }
  }, []);

  useEffect(() => {
    if (!showDialog || !hasMedicationSection) {
      setMedicationAxisLabels([]);
      return undefined;
    }

    const updateMedicationAxisLabels = () => {
      const chart = medicationChartRef.current;
      const yScale = chart?.scales?.y;
      const chartArea = chart?.chartArea;

      if (!yScale || !chartArea) {
        setMedicationAxisLabels([]);
        return;
      }

      setMedicationAxisLabels(
        medicationLaneEntries.map((entry) => ({
          key: entry.key,
          fullLabel: entry.label,
          top: yScale.getPixelForValue(entry.laneValue),
          plotLeft: chartArea.left,
        })),
      );
    };

    let frameId = window.requestAnimationFrame(updateMedicationAxisLabels);
    const handleResize = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateMedicationAxisLabels);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
    };
  }, [showDialog, hasMedicationSection, medicationLaneEntries, medicationChartCanvasHeight]);

  useEffect(() => {
    if (!hasMedicationSection) {
      setActiveMedicationAxisLabel(null);
    }
  }, [hasMedicationSection]);

  const sharedTooltipCallbacks = useMemo(() => ({
    title: (items) => {
      const point = items?.[0]?.raw;
      return point?.meta?.type === "medication"
        ? t("medication", { defaultValue: "Medicine" })
        : t("symptom", { defaultValue: "Symptom" });
    },
    label: (context) => {
      const meta = context?.raw?.meta;

      if (!meta) {
        return "";
      }

      if (meta.type === "medication") {
        const hasExplicitEndDate = meta.hasExplicitEndDate === true;
        const hasSingleDate = isSameMoment(meta.startDate, meta.endDate);

        return [
          `${t("product", { defaultValue: "Product" })}: ${meta.productName}`,
          `${t("active_substance", { defaultValue: "Active substance" })}: ${formatActiveSubstanceLabel(meta.activeSubstance)}`,
          ...(hasExplicitEndDate
            ? (hasSingleDate
              ? [`${t("date", { defaultValue: "Date" })}: ${formatTooltipDateOnly(meta.startDate)}`]
              : [
                `${t("start_date", { defaultValue: "Start" })}: ${formatTooltipDateOnly(meta.startDate)}`,
                `${t("end_date", { defaultValue: "End" })}: ${formatTooltipDateOnly(meta.endDate)}`,
              ])
            : [`${t("start_date", { defaultValue: "Start" })}: ${formatTooltipDateOnly(meta.startDate)}`]),
        ];
      }

      const hasSingleDate = isSameMoment(meta.startDate, meta.endDate);
      const symptomDateText = hasSingleDate
        ? formatTooltipDateOnly(meta.startDate)
        : `${formatTooltipDateOnly(meta.startDate)} - ${formatTooltipDateOnly(meta.endDate)}`;

      return [
        `${meta.term}`,
        `${t("severity", { defaultValue: "Severity" })}: ${meta.severity}`,
        `${symptomDateText}`,
      ];
    },
  }), [t]);

  const symptomChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: {
        padding: {
          left: isMobile ? 48 : 64,
          right: isMobile ? 8 : 12,
        },
      },
      interaction: {
        mode: "nearest",
        // Showing tooltips only if the mouse pointer really above the chart is
        intersect: true,
      },
      scales: {
        x: {
          type: "time",
          min: viewRange?.min,
          max: viewRange?.max,
          time: {
            unit: "day",
            tooltipFormat: "dd.MM.yyyy HH:mm",
            displayFormats: {
              day: "dd.MM.yyyy",
            },
          },
          ticks: {
            callback: (value) => {
              const tickDate = new Date(value);
              return isValid(tickDate) ? format(tickDate, "dd.MM.yyyy") : "";
            },
            autoSkip: true,
            minRotation: CHART_TICK_ROTATION_DEG,
            maxRotation: CHART_TICK_ROTATION_DEG,
            display: true,
          },
          title: {
            display: true,
            text: t("date", { defaultValue: "Date" }),
          },
          grid: {
            display: true,
            color: "#e3f2fd",
          },
        },
        y: {
          min: SYMPTOM_MIN - 0.5,
          max: SYMPTOM_MAX + 0.5,
          afterFit: (axis) => {
            axis.width = chartAxisWidth;
          },
          afterBuildTicks: (axis) => {
            axis.ticks = Array.from(
              { length: SYMPTOM_MAX - SYMPTOM_MIN + 1 },
              (_, index) => ({ value: SYMPTOM_MIN + index }),
            );
          },
          ticks: {
            stepSize: 1,
            autoSkip: false,
            includeBounds: true,
            color: (ctx) => {
              const value = Number(ctx?.tick?.value);
              return value >= SYMPTOM_MIN && value <= SYMPTOM_MAX ? "#1565a0" : "#1565a0";
            },
            callback: (value) => {
              return value >= SYMPTOM_MIN && value <= SYMPTOM_MAX
                ? (isMobile ? `${value}` : `${t("severity", { defaultValue: "Severity" })} ${value}`)
                : "";
            },
          },
          grid: {
            color: "#e3f2fd",
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          filter: (tooltipItem) => {
            const isTooltipEnabled = tooltipItem?.dataset?.tooltipEnabled !== false;
            const metaType = tooltipItem?.raw?.meta?.type;
            return isTooltipEnabled && metaType === "symptom";
          },
          callbacks: sharedTooltipCallbacks,
        },
        zoom: {
          pan: {
            enabled: !isMobile,
            mode: "x",
            onPanComplete: ({ chart }) => createRangeUpdater(chart),
          },
          zoom: {
            wheel: {
              enabled: !isMobile,
            },
            pinch: {
              enabled: !isMobile,
            },
            mode: "x",
            onZoomComplete: ({ chart }) => createRangeUpdater(chart),
          },
        },
        symptomsMedicationBands: {
          sectionMode: "symptoms",
          medicationAxisLabel: "",
          severityAxisLabel: t("symptoms_severity_axis", { defaultValue: "Severity 1-5" }),
          axisLabelMinGutterX: isMobile ? 1 : 4,
          axisLabelOffsetX: isMobile ? 4 : 6,
          medicationLabelColor: "#8e24aa",
          severityLabelColor: "#1565a0",
          medicationsBackgroundColor: "#dceefe",
          symptomsBackgroundColor: "#f6fbff",
          separatorColor: "transparent",
        },
      },
    }),
    [chartAxisWidth, createRangeUpdater, hasMedicationSection, isMobile, sharedTooltipCallbacks, t, viewRange],
  );

  const medicationChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: {
        padding: {
          left: isMobile ? 48 : 64,
          right: isMobile ? 8 : 12,
        },
      },
      interaction: {
        mode: "nearest",
        intersect: true,
      },
      scales: {
        x: {
          type: "time",
          min: viewRange?.min,
          max: viewRange?.max,
          time: {
            unit: "day",
            tooltipFormat: "dd.MM.yyyy HH:mm",
            displayFormats: {
              day: "dd.MM.yyyy",
            },
          },
          ticks: {
            callback: (value) => {
              const tickDate = new Date(value);
              return isValid(tickDate) ? format(tickDate, "dd.MM.yyyy") : "";
            },
            autoSkip: true,
            maxRotation: 0,
            maxTicksLimit: isMobile ? 6 : 10,
            display: false,
          },
          title: {
            display: false,
            text: t("date", { defaultValue: "Date" }),
          },
          grid: {
            display: false,
            color: "#e3f2fd",
          },
        },
        y: {
          min: 0.5,
          max: Math.max(1, medicationLaneEntries.length) + 0.5,
          afterFit: (axis) => {
            axis.width = chartAxisWidth;
          },
          afterBuildTicks: (axis) => {
            axis.ticks = medicationLaneEntries.map(({ laneValue }) => ({ value: laneValue }));
          },
          ticks: {
            stepSize: 1,
            autoSkip: false,
            includeBounds: false,
            color: "#8e24aa",
            callback: () => "",
          },
          grid: {
            color: "#e3f2fd",
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          filter: (tooltipItem) => {
            const isTooltipEnabled = tooltipItem?.dataset?.tooltipEnabled !== false;
            return isTooltipEnabled && tooltipItem?.raw?.meta?.type === "medication";
          },
          callbacks: sharedTooltipCallbacks,
        },
        zoom: {
          pan: {
            enabled: true,
            mode: "x",
            onPanComplete: ({ chart }) => createRangeUpdater(chart),
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: "x",
            onZoomComplete: ({ chart }) => createRangeUpdater(chart),
          },
        },
        symptomsMedicationBands: {
          sectionMode: "medications",
          medicationAxisLabel: "",
          severityAxisLabel: "",
          axisLabelMinGutterX: isMobile ? 4 : 8,
          axisLabelOffsetX: isMobile ? 8 : 12,
          medicationLabelColor: "#8e24aa",
          severityLabelColor: "#1565a0",
          medicationsBackgroundColor: "#dceefe",
          symptomsBackgroundColor: "#f6fbff",
          separatorColor: "transparent",
        },
      },
    }),
    [chartAxisWidth, createRangeUpdater, isMobile, medicationLaneEntries, sharedTooltipCallbacks, t, viewRange],
  );

  const toggleVisibility = (setter, key) => {
    setter((prev) => ({ ...prev, [key]: !(prev[key] !== false) }));
  };

  const handleToggleShowMedications = useCallback(() => {
    setShowMedications((prev) => {
      const next = !prev;

      if (next) {
        // Set the checkboxes to deafult=true, if they are activated again
        setMedicationVisibility(buildInitialVisibility(medicationEntries.map((entry) => entry.key)));
      }

      return next;
    });
  }, [medicationEntries]);

  const shiftWindow = useCallback((direction) => {
    if (!viewRange) {
      return;
    }

    const spanMs = viewRange.max.getTime() - viewRange.min.getTime();
    const offset = spanMs * direction;

    setViewRange({
      min: new Date(viewRange.min.getTime() + offset),
      max: new Date(viewRange.max.getTime() + offset),
    });
  }, [viewRange]);

  const zoomByFactor = useCallback((factor) => {
    symptomChartRef.current?.zoom?.(factor);
    medicationChartRef.current?.zoom?.(factor);
  }, []);

  const resetZoom = useCallback(() => {
    symptomChartRef.current?.resetZoom?.();
    medicationChartRef.current?.resetZoom?.();
    setShowMedications(true);
    setMedicationVisibility(buildInitialVisibility(medicationEntries.map((entry) => entry.key)));
    setViewRange(initialRange);
  }, [initialRange, medicationEntries]);

  if (!showDialog) {
    return null;
  }

  return (
    <div
      className="w3-modal"
      style={{ display: "block", backgroundColor: "rgba(10, 30, 60, 0.35)" }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onCloseDialog();
        }
      }}
    >
      <div
        className={`w3-card-4 w3-animate-zoom timeline-chart-modal dialog-draggable ${isDragging ? "dialog-draggable--dragging" : ""}`}
        style={dragStyle}
      >
        <header
          className="dialog-drag-header"
          onMouseDown={onDragHandleMouseDown}
        >
          <strong className="dialog-drag-header__title">
            {t("symptoms_line_chart", { defaultValue: "Symptoms Timeline" })}
          </strong>
          <button
            type="button"
            className="dialog-drag-close"
            onClick={onCloseDialog}
            aria-label={t("close", { defaultValue: "Close" })}
            title={t("close", { defaultValue: "Close" })}
          >
            ×
          </button>
        </header>

        {error && <ErrorMessage message={t("error_by_retrieving_prescribed_drugs")} />}
        {loading && <Spinner />}

        <div className="timeline-chart-controls-panel">
          <div className="timeline-chart-toolbar">
            <button
              type="button"
              className="w3-button w3-round timeline-chart-toolbar-button"
              onClick={() => shiftWindow(-1)}
            >
              {"<"} {t("previous", { defaultValue: "Previous" })}
            </button>
            <button
              type="button"
              className="w3-button w3-round timeline-chart-toolbar-button"
              onClick={() => shiftWindow(1)}
            >
              {t("next", { defaultValue: "Next" })} {">"}
            </button>
            <button
              type="button"
              className="w3-button w3-round timeline-chart-toolbar-button"
              onClick={() => zoomByFactor(1.25)}
            >
              {t("zoom_in", { defaultValue: "Zoom +" })}
            </button>
            <button
              type="button"
              className="w3-button w3-round timeline-chart-toolbar-button"
              onClick={() => zoomByFactor(0.8)}
            >
              {t("zoom_out", { defaultValue: "Zoom -" })}
            </button>
            <button
              type="button"
              className="w3-button w3-round timeline-chart-toolbar-button"
              onClick={resetZoom}
            >
              {t("reset", { defaultValue: "Reset" })}
            </button>
            <label className="timeline-chart-checkbox">
              <input
                type="checkbox"
                checked={showMedications}
                onChange={handleToggleShowMedications}
              />
              {t("show_all_medications", { defaultValue: "Show all medicines" })}
            </label>
          </div>

          <div className="timeline-chart-legend-grid">
            <div className="w3-card timeline-chart-legend-card">
              <button
                type="button"
                onClick={() => setShowSymptomsCardContent((prev) => !prev)}
                aria-expanded={showSymptomsCardContent}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  padding: "6px 8px",
                  border: "1px solid #d6e9f6",
                  borderRadius: "8px",
                  background: "linear-gradient(180deg, #f8fbff 0%, #edf6fd 100%)",
                  boxShadow: "0 2px 8px rgba(21, 101, 160, 0.08)",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <strong>{t("symptoms", { defaultValue: "Symptoms" })}</strong>
                <span
                  style={{
                    minWidth: "24px",
                    height: "24px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "999px",
                    background: "#ffffff",
                    border: "1px solid #bfdbf2",
                    color: "#1565a0",
                    fontWeight: 700,
                    fontSize: "14px",
                    lineHeight: 1,
                  }}
                >
                  {showSymptomsCardContent ? "⌃" : "⌄"}
                </span>
              </button>
              {showSymptomsCardContent && (
                <div
                  className="timeline-chart-legend-list"
                  style={{
                    maxHeight: shouldScrollSymptomsLegend ? `${LEGEND_SCROLL_HEIGHT_PX}px` : "none",
                    overflowY: shouldScrollSymptomsLegend ? "scroll" : "visible",
                  }}
                >
                  {termKeys.map((term) => (
                    <label key={term} className="timeline-chart-legend-item">
                      <input
                        type="checkbox"
                        checked={termVisibility[term] !== false}
                        onChange={() => toggleVisibility(setTermVisibility, term)}
                      />
                      <span
                        className="timeline-chart-legend-dot"
                        style={{ background: stableColorForKey(term, SYMPTOM_PALETTE) }}
                      />
                      {t(term, { defaultValue: term })}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {showMedications && (
              <div className="w3-card timeline-chart-legend-card">
                <button
                  type="button"
                  onClick={() => setShowMedicationsCardContent((prev) => !prev)}
                  aria-expanded={showMedicationsCardContent}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                    padding: "6px 8px",
                    border: "1px solid #d6e9f6",
                    borderRadius: "8px",
                    background: "linear-gradient(180deg, #f8fbff 0%, #edf6fd 100%)",
                    boxShadow: "0 2px 8px rgba(21, 101, 160, 0.08)",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <strong>{t("medications", { defaultValue: "Medicines" })}</strong>
                  <span
                    style={{
                      minWidth: "24px",
                      height: "24px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "999px",
                      background: "#ffffff",
                      border: "1px solid #bfdbf2",
                      color: "#1565a0",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1,
                    }}
                  >
                    {showMedicationsCardContent ? "⌃" : "⌄"}
                  </span>
                </button>
                {showMedicationsCardContent && (
                  <div
                    className="timeline-chart-legend-list"
                    style={{
                      maxHeight: shouldScrollMedicationsLegend ? `${LEGEND_SCROLL_HEIGHT_PX}px` : "none",
                      overflowY: shouldScrollMedicationsLegend ? "scroll" : "visible",
                    }}
                  >
                    {medicationEntries.map((entry) => (
                      <label key={entry.key} className="timeline-chart-legend-item">
                        <input
                          type="checkbox"
                          checked={medicationVisibility[entry.key] !== false}
                          onChange={() => toggleVisibility(setMedicationVisibility, entry.key)}
                        />
                        <span
                          className="timeline-chart-legend-dot"
                          style={{ background: stableColorForKey(entry.activeSubstance, MEDICATION_PALETTE) }}
                        />
                        <span className="symptoms-medication-label-ellipsis">{entry.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          className="timeline-chart-canvas-wrap"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: hasMedicationSection ? `${CHART_SECTION_GAP_PX}px` : 0,
            minHeight: 0,
          }}
        >
          {hasMedicationSection && (
            <div
              style={{
                flex: `0 0 ${visibleMedicationAreaHeight}`,
                minHeight: 0,
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: "0px",
                  top: "50%",
                  transform: "translateY(-50%) rotate(-90deg)",
                  transformOrigin: "left center",
                  color: "#8e24aa",
                  fontWeight: 600,
                  fontSize: isMobile ? "0.7rem" : "0.78rem",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                  zIndex: 3,
                }}
              >
                {t("medications", { defaultValue: "Medicines" })}
              </div>
              <div
                style={{
                  height: "100%",
                  overflowY: "auto",
                  overflowX: "hidden",
                  position: "relative",
                  WebkitOverflowScrolling: "touch",
                  touchAction: "pan-y",
                  overscrollBehaviorY: "contain",
                }}
              >
              <div
                style={{
                  height: `max(100%, ${medicationChartCanvasHeight}px)`,
                  minHeight: "100%",
                  position: "relative",
                }}
              >
                <Line
                  ref={medicationChartRef}
                  data={medicationChartData}
                  options={medicationChartOptions}
                  plugins={[symptomsMedicationBandsPlugin]}
                />
                {medicationAxisLabels.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      overflow: "visible",
                      zIndex: 2,
                      pointerEvents: "none",
                    }}
                  >
                    {medicationAxisLabels.map((label) => (
                      <div
                        key={label.key}
                        title={label.fullLabel}
                        aria-label={label.fullLabel}
                        onMouseEnter={() => setActiveMedicationAxisLabel(label.key)}
                        onMouseLeave={() => setActiveMedicationAxisLabel((current) => (current === label.key ? null : current))}
                        onFocus={() => setActiveMedicationAxisLabel(label.key)}
                        onBlur={() => setActiveMedicationAxisLabel((current) => (current === label.key ? null : current))}
                        onClick={() => setActiveMedicationAxisLabel((current) => (current === label.key ? null : label.key))}
                        tabIndex={0}
                        style={{
                          position: "absolute",
                          left: `${Math.max(label.plotLeft - Math.max(chartAxisWidth - (isMobile ? 4 : 10), 20) - 1, 0)}px`,
                          top: `${label.top}px`,
                          width: `${Math.max(chartAxisWidth - (isMobile ? 4 : 10), isMobile ? 64 : 20)}px`,
                          display: "inline-block",
                          transform: isMobile
                            ? "translateY(-50%)"
                            : `translateY(-50%) rotate(${MEDICATION_LABEL_ROTATION_DEG}deg)`,
                          transformOrigin: "right center",
                          whiteSpace: isMobile ? "normal" : "nowrap",
                          overflow: isMobile ? "visible" : "hidden",
                          textOverflow: isMobile ? "clip" : "ellipsis",
                          textAlign: "right",
                          color: "#8e24aa",
                          fontSize: isMobile ? "0.62rem" : "0.78rem",
                          lineHeight: isMobile ? 1.15 : 1.1,
                          wordBreak: "normal",
                          overflowWrap: isMobile ? "anywhere" : "normal",
                          pointerEvents: isMobile ? "none" : "auto",
                          cursor: isMobile ? "default" : "help",
                        }}
                      >
                        {label.fullLabel}
                      </div>
                    ))}
                    {activeMedicationAxisLabel && (
                      <div
                        style={{
                          position: "absolute",
                          left: `${Math.max(
                            (medicationAxisLabels.find((label) => label.key === activeMedicationAxisLabel)?.plotLeft || 0) + 8,
                            8,
                          )}px`,
                          top: `${medicationAxisLabels.find((label) => label.key === activeMedicationAxisLabel)?.top || 0}px`,
                          transform: "translateY(-50%)",
                          maxWidth: isMobile ? "50vw" : "320px",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          background: "#ffffff",
                          border: "1px solid #bfdbf2",
                          boxShadow: "0 4px 12px rgba(15, 23, 42, 0.16)",
                          color: "#334155",
                          fontSize: isMobile ? "0.72rem" : "0.82rem",
                          lineHeight: 1.3,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          pointerEvents: "none",
                        }}
                      >
                        {medicationAxisLabels.find((label) => label.key === activeMedicationAxisLabel)?.fullLabel}
                      </div>
                    )}
                  </div>
                )}
              </div>
              </div>
            </div>
          )}

          <div
            style={{
              flex: hasMedicationSection ? `1 1 ${visibleSymptomAreaHeight}` : "1 1 auto",
              minHeight: 0,
            }}
          >
            <div style={{ height: "100%", minHeight: "100%" }}>
              <Line
                ref={symptomChartRef}
                data={symptomChartData}
                options={symptomChartOptions}
                plugins={[symptomsMedicationBandsPlugin]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SymptomsLineChart;
